import 'reactflow/dist/style.css';

import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, alpha, darken, lighten, makeStyles } from '@material-ui/core';
import Reactflow, { ReactFlowProvider } from 'reactflow';

import HopNode from './HopNode';
import UnidentifiableHopNode from './UnidentifiableHopNode';
import { generatePathElements, layoutPathElements } from './PathVisualizationUtilities';
import { ChartControls } from './ChartControls';
import useIsPrinting from '../../utilities/PrintUtilities';

const FIT_VIEW_MAX_ZOOM = 0.4;
const PATH_CHART_MIN_ZOOM = 0.1;

const useStyles = makeStyles((theme) => ({
    chartWrapper: {
        width: '100%',
        height: '100%',
        padding: '0 24px',
    },
    chart: {
        '& .react-flow__pane': {
            cursor: 'grab',
        },

        '& .react-flow__pane:active': {
            cursor: 'grabbing',
        },
    },
    edge: {
        '& > path': {
            stroke:
                theme.palette.type === 'light'
                    ? darken(alpha(theme.palette.divider, 1), 0.85)
                    : lighten(alpha(theme.palette.divider, 1), 0.85),
            strokeWidth: 2,
        },
    },
}));

export function PathVisualizationChart({ data, test, viewport, viewportKey }) {
    const isPrinting = useIsPrinting();
    const styles = useStyles();
    const nodeTypes = useMemo(() => ({ hop: HopNode, unidentifiableHop: UnidentifiableHopNode }), []);
    const [nodes, edges] = useMemo(() => generatePathElements(data, test), [data, test]);
    const laidOutNodes = useMemo(() => layoutPathElements(nodes, edges), [nodes, edges]);

    return (
        <Box className={styles.chartWrapper}>
            <ReactFlowProvider>
                <Reactflow
                    fitView={!viewport}
                    fitViewOptions={{ maxZoom: FIT_VIEW_MAX_ZOOM }}
                    className={styles.chart}
                    nodes={laidOutNodes}
                    nodeTypes={nodeTypes}
                    defaultViewport={viewport}
                    edges={edges}
                    defaultEdgeOptions={{ className: styles.edge }}
                    edgesFocusable={false}
                    zoomOnScroll={false}
                    zoomOnPinch={false}
                    nodesConnectable={false}
                    nodesDraggable={false}
                    preventScrolling={false}
                    minZoom={PATH_CHART_MIN_ZOOM}
                    proOptions={{ hideAttribution: true }}
                >
                    {!isPrinting && <ChartControls viewportKey={viewportKey} />}
                </Reactflow>
            </ReactFlowProvider>
        </Box>
    );
}

PathVisualizationChart.propTypes = {
    data: PropTypes.shape({
        agent: PropTypes.object,
        path: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
    test: PropTypes.object.isRequired,
    viewport: PropTypes.exact({
        x: PropTypes.number,
        y: PropTypes.number,
        zoom: PropTypes.number,
    }),
    // Used by parent components using viewport context to track viewports between renders. See PathVisualizationColumnRenderer.
    viewportKey: PropTypes.string,
};

PathVisualizationChart.defaultProps = {
    viewport: undefined,
    viewportKey: null,
};
